import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Fragment, useState, type ReactElement, type ReactNode } from "react";
import { ContentHeader, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { FiscalDocumentView, FiscalFileLink } from "@cx/contracts";
import { formatBRL, formatDateBR, formatDocument, formatQty, todayInSaoPaulo } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { InvoiceGnrePanel } from "../gnre/InvoiceGnrePanel";
import {
  ENVIRONMENT_LABEL,
  INVOICE_STATUS_LABEL,
  INVOICE_STATUS_TONE,
  accessKeyGroups,
  invoiceTitle,
  isInFlight,
  eventInFlight,
  sefazMessage,
} from "./labels";
import {
  CancelInvoiceDialog,
  CorrectionDialog,
  EventsTimeline,
  LinkedInvoices,
  ReturnDialog,
  VoidRangeDialog,
  dateTimeBR,
} from "./InvoiceEvents";

type Dialog = "cancel" | "correction" | "return" | "void" | null;

/**
 * NF-e (T2.3): situação com a mensagem da SEFAZ, itens, totais e pagamentos; enviar/emitir de novo (rascunho ou
 * rejeitada), consultar de novo (em envio) e baixar DANFE/XML (autorizada). A tela consulta sozinha enquanto a nota
 * está na fila ou em envio.
 *
 * T2.4: Cancelar (autorizada, no prazo), Carta de correção, Devolver (saldo devolvível) e Descartar e inutilizar
 * (rejeitada); linha do tempo dos eventos com XML/PDF. A tela consulta sozinha também enquanto há evento em envio.
 */
export function InvoicePage(): ReactElement {
  const { id } = useParams({ from: "/vendas/notas-fiscais/$id" });
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const doc = useQuery({
    queryKey: ["fiscal-documents", companyId, "view", id],
    refetchOnMount: "always",
    queryFn: () => api<FiscalDocumentView>(`/fiscal-documents/${id}`),
    refetchInterval: (query) => {
      const v = query.state.data;
      return v && (isInFlight(v.status) || v.events.some(eventInFlight)) ? 2500 : false;
    },
  });
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<Dialog>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["fiscal-documents", companyId] });
  const onError = (e: Error) => {
    toast("danger", errorMessage(e));
    void refresh();
  };
  // Uma chave por tela: o duplo clique não envia duas vezes.
  const [issueKey, setIssueKey] = useState(() => crypto.randomUUID());
  const issue = useMutation({
    mutationFn: () =>
      api<FiscalDocumentView>(`/fiscal-documents/${id}/issue`, {
        method: "POST",
        headers: { "Idempotency-Key": issueKey },
      }),
    onSuccess: (d) => {
      setIssueKey(crypto.randomUUID());
      qc.setQueryData(["fiscal-documents", companyId, "view", id], d);
      toast("info", `${invoiceTitle(d)} enviada à SEFAZ`);
      void refresh();
    },
    onError,
  });
  const retry = useMutation({
    mutationFn: () => api<FiscalDocumentView>(`/fiscal-documents/${id}/refresh`, { method: "POST" }),
    onSuccess: () => {
      toast("info", "Consultando a SEFAZ de novo");
      void refresh();
    },
    onError,
  });
  const download = useMutation({
    mutationFn: (file: "danfe" | "xml") => api<FiscalFileLink>(`/fiscal-documents/${id}/${file}`),
    onSuccess: (r) => window.location.assign(r.url),
    onError,
  });

  const d = doc.data;
  const canIssue = can(perms, "invoices", "create");
  const canUpdate = can(perms, "invoices", "update");
  const message = d ? sefazMessage(d.rejectionCode, d.rejectionReason) : null;
  const eventBusy = !!d?.events.some(eventInFlight);
  const now = Date.now();
  const canCancel =
    !!d && canUpdate && d.status === "authorized" && !!d.cancelUntil && Date.parse(d.cancelUntil) > now;
  const canCorrect =
    !!d &&
    canUpdate &&
    d.status === "authorized" &&
    !!d.correctionUntil &&
    Date.parse(d.correctionUntil) > now;
  const canReturn = !!d && canIssue && d.returnable.some((r) => r.qtyQ4 > 0);
  const canDiscard = !!d && canUpdate && d.status === "rejected" && d.number !== null && d.series !== null;
  const isReturn = d?.purpose === 4;
  const afterEvent = (v: FiscalDocumentView) => {
    setDialog(null);
    qc.setQueryData(["fiscal-documents", companyId, "view", id], v);
    void refresh();
  };
  return (
    <>
      <ContentHeader
        title={d ? invoiceTitle(d) : "Nota fiscal"}
        breadcrumb={["Vendas", "Notas fiscais", d?.number ? String(d.number) : ""]}
        actions={
          d && (
            <>
              <Link to="/vendas/notas-fiscais" className="btn btn-outline-secondary">
                Voltar
              </Link>
              {d.hasDanfe && (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={download.isPending}
                  onClick={() => download.mutate("danfe")}
                >
                  Baixar DANFE
                </button>
              )}
              {d.hasXml && (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={download.isPending}
                  onClick={() => download.mutate("xml")}
                >
                  Baixar XML
                </button>
              )}
              {canUpdate && d.canRefresh && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={retry.isPending}
                  onClick={() => retry.mutate()}
                >
                  Consultar de novo
                </button>
              )}
              {canCancel && (
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  disabled={eventBusy}
                  onClick={() => setDialog("cancel")}
                >
                  Cancelar NF-e
                </button>
              )}
              {canCorrect && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={eventBusy}
                  onClick={() => setDialog("correction")}
                >
                  Carta de correção
                </button>
              )}
              {canReturn && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setDialog("return")}
                >
                  Devolver
                </button>
              )}
              {canDiscard && (
                <button type="button" className="btn btn-outline-danger" onClick={() => setDialog("void")}>
                  Descartar e inutilizar
                </button>
              )}
              {canIssue && d.editable && (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={issue.isPending}
                  onClick={() => issue.mutate()}
                >
                  {d.status === "rejected" ? "Emitir de novo" : "Enviar à SEFAZ"}
                </button>
              )}
            </>
          )
        }
      />
      <QueryStatus loading={doc.isLoading} error={doc.error} />
      {d && (
        <div className="row">
          <div className="col-xl-9">
            <div className="box card p-3 mb-3" aria-live="polite">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span>Situação:</span>
                <StatusBadge tone={INVOICE_STATUS_TONE[d.status]}>
                  {INVOICE_STATUS_LABEL[d.status]}
                </StatusBadge>
                <span className={`badge ${d.environment === 2 ? "text-bg-warning" : "text-bg-secondary"}`}>
                  {ENVIRONMENT_LABEL[d.environment]}
                  {d.environment === 2 && " — sem valor fiscal"}
                </span>
                {isInFlight(d.status) && (
                  <span className="spinner-border spinner-border-sm text-info" role="status">
                    <span className="visually-hidden">Aguardando a SEFAZ</span>
                  </span>
                )}
              </div>
              {(d.status === "rejected" || d.status === "denied") && message && (
                <div className="alert alert-danger mb-2" role="alert" data-testid="sefaz-message">
                  <strong>
                    {d.status === "denied" ? "Uso denegado pela SEFAZ." : "Rejeitada pela SEFAZ."}
                  </strong>{" "}
                  {message}
                  {d.status === "rejected" && (
                    <div className="small mt-1">
                      Corrija o cadastro (produto, cliente, natureza) e use “Emitir de novo”: o número{" "}
                      {d.number} é mantido. Para desistir da nota, use “Descartar e inutilizar”.
                    </div>
                  )}
                </div>
              )}
              {d.status === "authorized" && (
                <div className="alert alert-success mb-2" role="status" data-testid="sefaz-message">
                  Autorizado o uso da NF-e{d.protocol && ` — protocolo ${d.protocol}`}
                  {d.authorizedAt && ` em ${dateTimeBR(d.authorizedAt)}`}.
                  {d.emailSentAt && (
                    <div className="small mt-1">DANFE e XML enviados ao cliente por e-mail.</div>
                  )}
                </div>
              )}
              {d.status === "canceled" && (
                <div className="alert alert-secondary mb-2" role="status" data-testid="sefaz-message">
                  NF-e cancelada na SEFAZ. A nota autorizada continua guardada; o cancelamento está nos
                  eventos.
                </div>
              )}
              {d.status === "voided" && (
                <div className="alert alert-secondary mb-2" role="status" data-testid="sefaz-message">
                  Número {d.number} inutilizado na SEFAZ: esta nota não será emitida.
                </div>
              )}
              {d.status === "authorized" && d.currentCorrection && (
                <div className="alert alert-light border mb-2" role="note" data-testid="current-correction">
                  <strong>Carta de correção vigente:</strong> {d.currentCorrection}
                </div>
              )}
              {isInFlight(d.status) && (
                <div className="alert alert-info mb-2" role="status">
                  {d.statusMessage ?? "Nota enviada. Aguardando a resposta da SEFAZ…"}
                </div>
              )}
              {d.effectsError && (
                <div className="alert alert-warning mb-2" role="alert">
                  {d.status === "canceled"
                    ? `A nota foi cancelada, mas o estoque/contas não foram estornados: ${d.effectsError}`
                    : isReturn
                      ? `A devolução foi autorizada, mas a entrada no estoque/crédito não foram lançados: ${d.effectsError}`
                      : `A nota foi autorizada, mas o estoque/contas do pedido não foram lançados: ${d.effectsError}`}
                </div>
              )}
              <dl className="row mb-0">
                <Info label="Natureza da operação">{d.natureName}</Info>
                <Info label="Destinatário">
                  {d.recipient?.name ?? d.recipientName}
                  {d.recipientDocument && (
                    <span className="text-muted ms-2">{formatDocument(d.recipientDocument)}</span>
                  )}
                </Info>
                <Info label="Endereço">{d.recipient?.address}</Info>
                <Info label="Emissão">
                  {d.issuedAt && formatDateBR(todayInSaoPaulo(new Date(d.issuedAt)))}
                </Info>
                <Info label="Chave de acesso">
                  {d.accessKey && (
                    <span className="font-monospace" data-testid="access-key">
                      {accessKeyGroups(d.accessKey)}
                    </span>
                  )}
                </Info>
                <Info label="Origem">
                  {d.origin?.type === "fiscal_document" ? (
                    <Link to="/vendas/notas-fiscais/$id" params={{ id: d.origin.id }}>
                      Devolução da NF-e nº {d.origin.number ?? "—"}
                    </Link>
                  ) : d.origin?.type === "service_order" ? (
                    <Link to="/servicos/ordens-de-servico/$id" params={{ id: d.origin.id }}>
                      Ordem de serviço nº {d.origin.number}
                    </Link>
                  ) : d.origin?.type === "grouped_billing" ? (
                    <Link to="/financas/faturamento-agrupado/$id" params={{ id: d.origin.id }}>
                      Faturamento agrupado nº {d.origin.number ?? "—"}
                    </Link>
                  ) : d.origin ? (
                    <Link to="/vendas/pedidos/$id" params={{ id: d.origin.id }}>
                      Pedido de venda nº {d.origin.number ?? "—"}
                    </Link>
                  ) : (
                    "NF-e avulsa"
                  )}
                </Info>
                {(d.references.length > 0 || d.returns.length > 0) && (
                  <Info label="Notas ligadas">
                    <LinkedInvoices doc={d} />
                  </Info>
                )}
              </dl>
            </div>
            <div className="box card mb-3">
              <div className="table-responsive">
                <table className="table align-middle mb-0" aria-label="Itens da nota">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Descrição</th>
                      <th scope="col">NCM</th>
                      <th scope="col">CFOP</th>
                      <th scope="col" className="text-end">
                        Qtd
                      </th>
                      <th scope="col" className="text-end">
                        Preço un.
                      </th>
                      <th scope="col" className="text-end">
                        ICMS
                      </th>
                      <th scope="col" className="text-end">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.items.map((it) => (
                      <tr key={it.position}>
                        <td>{it.position}</td>
                        <td>
                          {it.description}
                          {it.code && <span className="text-muted small ms-2">{it.code}</span>}
                        </td>
                        <td>{it.ncm ?? "—"}</td>
                        <td>{it.cfop}</td>
                        <td className="text-end">
                          {formatQty(it.qtyQ4)} {it.unit}
                        </td>
                        <td className="text-end">{formatBRL(it.unitPriceCents)}</td>
                        <td className="text-end">{formatBRL(it.icmsCents)}</td>
                        <td className="text-end">{formatBRL(it.totalCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {d.payments.length > 0 && (
              <div className="box card p-3 mb-3">
                <h2 className="h6">Pagamento</h2>
                <table className="table table-sm w-auto mb-0" aria-label="Pagamentos">
                  <thead>
                    <tr>
                      <th scope="col">Forma</th>
                      <th scope="col">Vencimento</th>
                      <th scope="col" className="text-end">
                        Valor
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.payments.map((p, k) => (
                      <tr key={k}>
                        <td>{p.name}</td>
                        <td>{formatDateBR(p.dueDate)}</td>
                        <td className="text-end">{formatBRL(p.amountCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <InvoiceGnrePanel fiscalDocumentId={d.id} invoiceStatus={d.status} />
            {d.events.length > 0 && (
              <div className="box card p-3 mb-3">
                <h2 className="h6">Eventos</h2>
                <EventsTimeline events={d.events} canUpdate={canUpdate} onChanged={() => void refresh()} />
              </div>
            )}
            {d.complementaryInfo && (
              <div className="box card p-3 mb-3">
                <h2 className="h6">Informações complementares</h2>
                <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                  {d.complementaryInfo}
                </p>
              </div>
            )}
          </div>
          <aside className="col-xl-3">
            <div className="box card p-3 mb-3">
              <h2 className="h6">Totais</h2>
              {d.totals ? (
                <dl className="row mb-0 small">
                  {(
                    [
                      ["Produtos", d.totals.productsCents],
                      ["Desconto", -d.totals.discountCents],
                      ["Frete", d.totals.freightCents],
                      ["Seguro", d.totals.insuranceCents],
                      ["Outras despesas", d.totals.otherCents],
                      ["Base do ICMS", d.totals.baseCents],
                      ["ICMS", d.totals.icmsCents],
                      ["ICMS-ST", d.totals.stCents],
                      ["IPI", d.totals.ipiCents],
                    ] as const
                  ).map(([label, v]) => (
                    <Fragment key={label}>
                      <dt className="col-7 fw-normal">{label}</dt>
                      <dd className="col-5 text-end mb-1">{formatBRL(v)}</dd>
                    </Fragment>
                  ))}
                  <dt className="col-7">Total da nota</dt>
                  <dd className="col-5 text-end fw-bold mb-0" data-testid="invoice-total">
                    {formatBRL(d.totalCents)}
                  </dd>
                </dl>
              ) : (
                <p className="mb-0 fw-bold">{formatBRL(d.totalCents)}</p>
              )}
            </div>
          </aside>
        </div>
      )}
      {d && (
        <>
          <CancelInvoiceDialog
            open={dialog === "cancel"}
            doc={d}
            onClose={() => setDialog(null)}
            onDone={afterEvent}
          />
          <CorrectionDialog
            open={dialog === "correction"}
            doc={d}
            onClose={() => setDialog(null)}
            onDone={afterEvent}
          />
          <ReturnDialog
            open={dialog === "return"}
            doc={d}
            onClose={() => setDialog(null)}
            onDone={(r) => {
              setDialog(null);
              void refresh();
              void navigate({ to: "/vendas/notas-fiscais/$id", params: { id: r.id } });
            }}
          />
          {d.number !== null && d.series !== null && (
            <VoidRangeDialog
              open={dialog === "void"}
              series={d.series}
              from={d.number}
              to={d.number}
              onClose={() => setDialog(null)}
              onDone={() => {
                setDialog(null);
                void refresh();
              }}
            />
          )}
        </>
      )}
    </>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="col-sm-4 col-lg-3 fw-normal text-muted">{label}</dt>
      <dd className="col-sm-8 col-lg-9">{children || "—"}</dd>
    </>
  );
}
